import React, { useState, useEffect, useRef } from 'react';
import {
    Alert,
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    LinearProgress,
    Chip,
    Button,
    Divider,
    Tooltip,
    Tabs,
    Tab,
    ListSubheader,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Close as CloseIcon,
    CheckCircle as CompletedIcon,
    PlayCircle as InProgressIcon,
    RadioButtonUnchecked as NotStartedIcon,
    ArrowBack as BackIcon,
    ArrowForward as NextIcon,
    NoteAlt as NoteIcon,
    SmartToy as TutorIcon,
    EmojiEvents as TrophyIcon,
    AccessTime as TimeIcon,
    School as LessonsIcon,
    Star as ScoreIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useGetIdentity } from 'react-admin';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import LessonPlayer from './LessonPlayer';
import { NoteSidebar } from '../Notes';
import { TutorChat } from '../Tutor';
import { AvailableInChips } from '../Courses/AvailableInChips';
import { RatingDisplay, RatingDialog } from '../Ratings';
import { ratingService, RatingSummary } from '../../services/ratingService';
import { devLog } from '../../utils/devLog';
import { displayEnrollmentMode } from '../../utils/enrollmentMode';

interface Course {
    contentFormat?: 'classic' | 'blocks';
    sections?: { id: number; title: string; position: number }[];
    id: number;
    title: string;
    description?: string;
    aiTutorEnabled?: boolean;
    enrollmentMode?: string;
    requireSequentialCompletion?: boolean;
}

interface Lesson {
    id: number;
    title: string;
    type: string;
    description?: string;
    content?: any;
    sortOrder: number;
    isMandatory: boolean;
    hasPeerReview?: boolean;
    peerReviewCriteria?: { name: string; description: string }[];
    aiTutorEnabled?: boolean | null; // null = inherit from course
    section?: string | number | { id: number; title?: string; position?: number } | null;
}

interface LessonProgress {
    id: number;
    lessonId: number;
    status: string; // not_started, in_progress, completed
    progressPercentage: number;
}

const DRAWER_WIDTH = 320;
const NOTES_DRAWER_MIN_WIDTH = 300;
const NOTES_DRAWER_DEFAULT_WIDTH = 400;
const NOTES_DRAWER_MAX_WIDTH = 700;
const TOP_NAVBAR_HEIGHT = 80; // Height of fixed top navbar
const BOTTOM_NAVBAR_HEIGHT = 56; // Height of fixed bottom navbar

const CoursePlayer: React.FC = () => {
    const { courseId } = useParams<{ courseId: string }>();
    const [searchParams] = useSearchParams();
    const isPreviewMode = searchParams.get('preview') === 'true';
    const [course, setCourse] = useState<Course | null>(null);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
    const [lessonProgress, setLessonProgress] = useState<Map<number, LessonProgress>>(new Map());
    // Mobile (< md): Inhaltsverzeichnis startet eingeklappt, sonst bleibt 70 px für den Inhalt (Design-Sweep)
    const [drawerOpen, setDrawerOpen] = useState(() => !(typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 899.95px)')?.matches));
    const [rightDrawerOpen, setRightDrawerOpen] = useState(false);
    const [rightDrawerTab, setRightDrawerTab] = useState<'notes' | 'tutor'>('notes');
    const [rightDrawerWidth, setRightDrawerWidth] = useState(NOTES_DRAWER_DEFAULT_WIDTH);
    const isResizingRef = useRef(false);
    const [loading, setLoading] = useState(true);
    const [enrollmentStatus, setEnrollmentStatus] = useState<string | null>(null); // null = loading, 'none' = not enrolled
    const [enrolling, setEnrolling] = useState(false);
    const [showCompletion, setShowCompletion] = useState(false);
    const [ratingSummary, setRatingSummary] = useState<RatingSummary | null>(null);
    const [ratingPromptOpen, setRatingPromptOpen] = useState(false);
    const [assignmentInfo, setAssignmentInfo] = useState<{ notes?: string; dueDate?: string } | null>(null);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const { data: identity } = useGetIdentity();

    useEffect(() => {
        devLog('CoursePlayer - courseId from URL:', courseId, 'type:', typeof courseId);
        if (courseId) {
            const parsedId = parseInt(courseId);
            devLog('CoursePlayer - parsed ID:', parsedId, 'isNaN:', isNaN(parsedId));
            loadCourse();
            loadLessons();
            // Load average rating for this course
            ratingService.getRatingSummary(parsedId)
                .then(setRatingSummary)
                .catch(() => {});
            // Load assignment info (notes, due date) for this course
            const token = localStorage.getItem('token');
            let assignUserId: number | null = null;
            if (token) {
                try { assignUserId = JSON.parse(atob(token.split('.')[1])).userId ?? null; } catch {}
            }
            const userFilter = assignUserId ? `&userId=${assignUserId}` : '';
            fetch(`${process.env.REACT_APP_API_URL}/api/course_assignments?course.id=${parsedId}&status[]=assigned&status[]=in_progress${userFilter}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/ld+json' },
            })
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    const members = data?.['hydra:member'] ?? data?.['member'] ?? [];
                    if (members.length > 0) {
                        setAssignmentInfo({
                            notes: members[0].notes || undefined,
                            dueDate: members[0].dueDate || undefined,
                        });
                    }
                })
                .catch(() => {});
            if (!isPreviewMode) {
                loadEnrollmentStatus();
            } else {
                setEnrollmentStatus('enrolled');
            }
        }
    }, [courseId]);

    // useGetIdentity() ist async: bei Hard-Reload ist identity beim ersten Lauf
    // noch undefined; ein reiner [courseId]-Effect würde Fortschritt dann nie
    // laden und alle Lektionen zeigten 0 %. Nachladen, sobald identity da ist.
    useEffect(() => {
        if ((identity as any)?.id && lessons.length > 0) {
            loadProgress(lessons.map(l => l.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identity, lessons]);

    // Resize handler for right drawer
    const handleResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        isResizingRef.current = true;
        const startX = e.clientX;
        const startWidth = rightDrawerWidth;

        const onMouseMove = (moveEvent: MouseEvent) => {
            if (!isResizingRef.current) return;
            const delta = startX - moveEvent.clientX;
            const newWidth = Math.min(NOTES_DRAWER_MAX_WIDTH, Math.max(NOTES_DRAWER_MIN_WIDTH, startWidth + delta));
            setRightDrawerWidth(newWidth);
        };

        const onMouseUp = () => {
            isResizingRef.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    };

    const loadCourse = async () => {
        try {
            const parsedId = parseInt(courseId!);
            devLog('loadCourse - attempting to load course with ID:', parsedId);
            if (isNaN(parsedId) || parsedId === 0) {
                throw new Error(`Invalid course ID: ${courseId}`);
            }
            const { data } = await dataProvider.getOne('courses', { id: parsedId });
            setCourse(data);
        } catch (error) {
            console.error('loadCourse error:', error);
            notify('Fehler beim Laden des Kurses', { type: 'error' });
        }
    };

    const loadEnrollmentStatus = async () => {
        try {
            const token = localStorage.getItem('token');
            let userId: number | null = null;
            if (token) {
                try { userId = JSON.parse(atob(token.split('.')[1])).userId ?? null; } catch {}
            }
            const { data } = await dataProvider.getList('course_enrollments', {
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'DESC' },
                filter: { 'course.id': parseInt(courseId!), ...(userId ? { userId } : {}) }
            });
            if (data.length > 0) {
                setEnrollmentStatus(data[0].status);
            } else {
                setEnrollmentStatus('none');
            }
        } catch {
            setEnrollmentStatus('none');
        }
    };

    const handleSelfEnroll = async () => {
        if (!course) return;
        setEnrolling(true);
        try {
            const { data } = await dataProvider.create('course_enrollments', {
                data: { course: `/api/courses/${courseId}`, status: 'enrolled' }
            });
            if (data?.status === 'pending_approval') {
                setEnrollmentStatus('pending_approval');
                notify('Einschreibungsanfrage gesendet. Sie werden benachrichtigt, sobald sie genehmigt wird.', { type: 'info' });
            } else {
                setEnrollmentStatus('enrolled');
                notify('Erfolgreich eingeschrieben!', { type: 'success' });
            }
        } catch (error: any) {
            const msg = error?.body?.['hydra:description'] || error?.message || 'Fehler beim Einschreiben';
            notify(msg, { type: 'error' });
        } finally {
            setEnrolling(false);
        }
    };

    const loadLessons = async () => {
        setLoading(true);
        try {
            const { data } = await dataProvider.getList('lessons', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'sortOrder', order: 'ASC' },
                filter: { course: `/api/courses/${courseId}` }
            });
            setLessons(data);
            const lessonParam = searchParams.get('lesson');
            if (lessonParam) {
                const idx = data.findIndex((l: any) => l.id === parseInt(lessonParam));
                if (idx >= 0) setCurrentLessonIndex(idx);
            }
        } catch (error) {
            notify('Fehler beim Laden der Lektionen', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const loadProgress = async (lessonIds: number[]) => {
        try {
            const token = localStorage.getItem('token');
            const userId = (identity as any)?.id;

            if (!userId) {
                console.error('No user ID found');
                return;
            }

            // Load progress for all lessons in parallel
            const progressPromises = lessonIds.map(async (lessonId) => {
                try {
                    const response = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/progress/lesson/${lessonId}?userId=${userId}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Accept': 'application/json'
                            }
                        }
                    );

                    if (response.ok) {
                        const data = await response.json();
                        return {
                            id: data.id || 0,
                            lessonId: lessonId,
                            status: data.status || 'not_started',
                            progressPercentage: parseFloat(data.progressPercentage || '0')
                        };
                    } else {
                        // No progress found yet - return default
                        return {
                            id: 0,
                            lessonId: lessonId,
                            status: 'not_started',
                            progressPercentage: 0
                        };
                    }
                } catch (err) {
                    console.error(`Error loading progress for lesson ${lessonId}:`, err);
                    return {
                        id: 0,
                        lessonId: lessonId,
                        status: 'not_started',
                        progressPercentage: 0
                    };
                }
            });

            const progressArray = await Promise.all(progressPromises);
            const progressMap = new Map<number, LessonProgress>();
            progressArray.forEach(progress => {
                progressMap.set(progress.lessonId, progress);
            });

            setLessonProgress(progressMap);
        } catch (error) {
            console.error('Error loading progress:', error);
        }
    };

    const currentLesson = lessons[currentLessonIndex];
    const currentProgress = currentLesson ? lessonProgress.get(currentLesson.id) : null;

    // Switch to notes tab if tutor becomes disabled for the current lesson
    useEffect(() => {
        if (rightDrawerTab === 'tutor' && currentLesson) {
            const tutorEnabled = currentLesson.aiTutorEnabled !== null && currentLesson.aiTutorEnabled !== undefined
                ? currentLesson.aiTutorEnabled
                : (course?.aiTutorEnabled ?? true);
            if (!tutorEnabled) {
                setRightDrawerTab('notes');
            }
        }
    }, [currentLessonIndex, currentLesson, course, rightDrawerTab]);

    // Determine if AI tutor is enabled for the current lesson
    // Lesson setting takes precedence, otherwise use course setting
    const isAiTutorEnabled = (): boolean => {
        if (!currentLesson) return false;
        // If lesson has explicit setting, use it
        if (currentLesson.aiTutorEnabled !== null && currentLesson.aiTutorEnabled !== undefined) {
            return currentLesson.aiTutorEnabled;
        }
        // Otherwise, inherit from course (default to true if not set)
        return course?.aiTutorEnabled ?? true;
    };

    // Generic function to update lesson progress (for partial updates, xAPI events, etc.)
    const updateLessonProgress = async (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => {
        try {
            const token = localStorage.getItem('token');
            const userId = (identity as any)?.id;

            if (!userId) {
                console.error('No user ID found');
                return false;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/progress/lesson/${lessonId}`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({
                        userId,
                        ...progressData
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();

                // Update local state
                const updatedProgress = new Map(lessonProgress);
                const currentProg = updatedProgress.get(lessonId) || {
                    id: 0,
                    lessonId,
                    status: 'not_started',
                    progressPercentage: 0
                };

                updatedProgress.set(lessonId, {
                    ...currentProg,
                    status: data.status || currentProg.status,
                    progressPercentage: parseFloat(data.progressPercentage || currentProg.progressPercentage)
                });
                setLessonProgress(updatedProgress);

                return true;
            } else {
                console.error('Failed to update progress:', response.status);
                return false;
            }
        } catch (error) {
            console.error('Error updating progress:', error);
            return false;
        }
    };

    const handleLessonComplete = async () => {
        if (!currentLesson) return;

        try {
            const token = localStorage.getItem('token');
            const userId = (identity as any)?.id;

            if (!userId) {
                console.error('No user ID found');
                return;
            }

            // Mark lesson as complete on backend
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/progress/lesson/${currentLesson.id}/complete`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ userId })
                }
            );

            if (response.ok) {
                // Update local state
                const updatedProgress = new Map(lessonProgress);
                updatedProgress.set(currentLesson.id, {
                    ...currentProgress!,
                    status: 'completed',
                    progressPercentage: 100
                });
                setLessonProgress(updatedProgress);

                notify('Lektion abgeschlossen!', { type: 'success' });

                // Check if all lessons are now completed
                const allCompleted = lessons.every(l => {
                    if (l.id === currentLesson.id) return true; // just completed
                    const p = updatedProgress.get(l.id);
                    return p?.status === 'completed';
                });

                if (allCompleted) {
                    const finishedCourseId = course?.id;
                    setTimeout(() => {
                        setShowCompletion(true);
                        if (!finishedCourseId) {
                            return;
                        }
                        ratingService.getMyRating(finishedCourseId)
                            .then(existing => {
                                if (!existing) {
                                    setRatingPromptOpen(true);
                                }
                            })
                            .catch(() => {});
                    }, 800);
                } else if (currentLessonIndex < lessons.length - 1) {
                    setTimeout(() => goToNextLesson(), 1000);
                }
            } else {
                // Autorentool v2: der Server prüft completionRule/minScore (422) und
                // sequentielles Locking (403) — die Meldung kommt vom Server.
                let serverMessage: string | null = null;
                try {
                    const body = await response.json();
                    serverMessage = body?.message ?? null;
                } catch { /* kein JSON */ }
                notify(serverMessage ?? 'Fehler beim Speichern des Fortschritts', { type: serverMessage ? 'warning' : 'error' });
            }
        } catch (error) {
            console.error('Error completing lesson:', error);
            notify('Fehler beim Speichern des Fortschritts', { type: 'error' });
        }
    };

    // Issue #382: Sequenzieller Abschluss — eine Lektion ist gesperrt, solange
    // eine vorherige Lektion nicht abgeschlossen ist. Im Preview-Modus
    // (Trainer-Vorschau) wird nicht gesperrt.
    const isLessonLocked = (index: number): boolean => {
        if (isPreviewMode || !course?.requireSequentialCompletion) return false;
        for (let i = 0; i < index; i++) {
            const prev = lessons[i];
            if (!prev) continue;
            if (lessonProgress.get(prev.id)?.status !== 'completed') {
                return true;
            }
        }
        return false;
    };

    const goToNextLesson = () => {
        const nextIndex = currentLessonIndex + 1;
        if (nextIndex < lessons.length && !isLessonLocked(nextIndex)) {
            setCurrentLessonIndex(nextIndex);
        }
    };

    const goToPreviousLesson = () => {
        if (currentLessonIndex > 0) {
            setCurrentLessonIndex(currentLessonIndex - 1);
        }
    };

    // Autorentool v2 (plan §6.8): v2-Kurse zeigen ihre Sektionen als Zwischenüberschriften.
    // Die globale sortOrder bleibt die Reihenfolge; Sektionen gruppieren nur die Anzeige.
    const lessonGroups = React.useMemo(() => {
        const sectionIdOf = (l: Lesson): number | null => {
            const ref = l.section;
            if (ref == null) return null;
            if (typeof ref === 'number') return ref;
            if (typeof ref === 'object') return ref.id;
            const m = /(\d+)$/.exec(ref);
            return m ? Number(m[1]) : null;
        };
        const titles = new Map<number, string>();
        (course?.sections ?? []).forEach((sec) => titles.set(sec.id, sec.title));
        lessons.forEach((l) => {
            if (l.section && typeof l.section === 'object' && l.section.title) titles.set(l.section.id, l.section.title);
        });
        const groups: { key: string; title: string | null; entries: { lesson: Lesson; index: number }[] }[] = [];
        lessons.forEach((lesson, index) => {
            const sid = course?.contentFormat === 'blocks' ? sectionIdOf(lesson) : null;
            const key = sid === null ? 'none' : `s${sid}`;
            let group = groups[groups.length - 1];
            if (!group || group.key !== key) {
                group = { key, title: sid === null ? null : (titles.get(sid) ?? ''), entries: [] };
                groups.push(group);
            }
            group.entries.push({ lesson, index });
        });
        return groups;
    }, [lessons, course]);

    const getStatusIcon = (lessonId: number) => {
        const progress = lessonProgress.get(lessonId);
        if (!progress) return <NotStartedIcon color="disabled" />;

        switch (progress.status) {
            case 'completed':
                return <CompletedIcon color="success" />;
            case 'in_progress':
                return <InProgressIcon color="primary" />;
            default:
                return <NotStartedIcon color="disabled" />;
        }
    };

    const calculateOverallProgress = () => {
        if (lessons.length === 0) return 0;

        let totalProgress = 0;
        lessons.forEach(lesson => {
            const progress = lessonProgress.get(lesson.id);
            totalProgress += progress?.progressPercentage || 0;
        });

        return Math.round(totalProgress / lessons.length);
    };

    if (loading || (!isPreviewMode && enrollmentStatus === null)) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: `calc(100vh - ${TOP_NAVBAR_HEIGHT}px - ${BOTTOM_NAVBAR_HEIGHT}px)`,
                marginTop: `${TOP_NAVBAR_HEIGHT}px`
            }}>
                <Typography>Lade Kurs...</Typography>
            </Box>
        );
    }

    // Enrollment access check (skip in preview mode)
    if (!isPreviewMode && enrollmentStatus !== null && enrollmentStatus !== 'enrolled' && enrollmentStatus !== 'in_progress' && enrollmentStatus !== 'completed') {
        const mode = displayEnrollmentMode((course as any)?.enrollmentMode || 'none');
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 8 }}>
                <Alert severity={enrollmentStatus === 'pending_approval' ? 'info' : 'warning'} sx={{ mb: 3 }}>
                    {enrollmentStatus === 'pending_approval' && 'Ihre Einschreibungsanfrage wird bearbeitet. Sie erhalten eine Benachrichtigung, sobald sie genehmigt wurde.'}
                    {enrollmentStatus === 'rejected' && 'Ihre Einschreibungsanfrage wurde abgelehnt. Sie können eine neue Anfrage stellen.'}
                    {enrollmentStatus === 'none' && mode === 'none' && 'Sie sind nicht in diesem Kurs eingeschrieben. Wenden Sie sich an Ihren Administrator.'}
                    {enrollmentStatus === 'none' && (mode === 'self' || mode === 'request') && `Sie sind nicht in diesem Kurs eingeschrieben.`}
                </Alert>
                <Typography variant="h5" gutterBottom>{course?.title || 'Kurs'}</Typography>
                {enrollmentStatus === 'none' && mode === 'self' && (
                    <Button variant="contained" onClick={handleSelfEnroll} disabled={enrolling} sx={{ mr: 2 }}>
                        {enrolling ? 'Einschreiben...' : 'Einschreiben'}
                    </Button>
                )}
                {(enrollmentStatus === 'none' || enrollmentStatus === 'rejected') && mode === 'request' && (
                    <Button variant="outlined" onClick={handleSelfEnroll} disabled={enrolling} sx={{ mr: 2 }}>
                        {enrolling ? 'Anfrage wird gesendet...' : 'Einschreibung anfragen'}
                    </Button>
                )}
                {/* #526: authors landed here from the builder/course management with no way back */}
                <Button onClick={() => navigate(-1)} variant="outlined" sx={{ mr: 2 }}>
                    Zurück
                </Button>
                <Button onClick={() => navigate('/catalog')} variant="outlined" sx={{ mr: 2 }}>
                    Zum Kurskatalog
                </Button>
                <Button onClick={() => navigate('/my-courses')}>
                    Meine Kurse
                </Button>
            </Box>
        );
    }

    if (!course || lessons.length === 0) {
        // Kurs geladen aber leer ≠ Kurs nicht gefunden. Zuweisungen können auf
        // einen noch inhaltslosen Kurs zeigen (#323) — dann keine 404-Meldung.
        const message = course
            ? 'Dieser Kurs enthält noch keine Lektionen.'
            : 'Kurs oder Lektionen nicht gefunden';
        return (
            <Box sx={{ p: 3 }}>
                <Typography>{message}</Typography>
                <Button onClick={() => navigate('/my-courses')} sx={{ mt: 2 }}>
                    Zurück zu meinen Kursen
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            height: `calc(100vh - ${TOP_NAVBAR_HEIGHT}px - ${BOTTOM_NAVBAR_HEIGHT}px)`,
            marginTop: `${TOP_NAVBAR_HEIGHT}px`,
            marginBottom: `${BOTTOM_NAVBAR_HEIGHT}px`,
        }}>
            {/* Sidebar */}
            <Drawer
                variant="persistent"
                anchor="left"
                open={drawerOpen}
                sx={{
                    width: drawerOpen ? DRAWER_WIDTH : 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        top: `${TOP_NAVBAR_HEIGHT}px`,
                        height: `calc(100vh - ${TOP_NAVBAR_HEIGHT}px - ${BOTTOM_NAVBAR_HEIGHT}px)`,
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" noWrap>
                            Inhaltsverzeichnis
                        </Typography>
                        <IconButton onClick={() => setDrawerOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {course.title}
                    </Typography>
                    <AvailableInChips courseId={course.id} />

                    <Box sx={{ my: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Gesamtfortschritt
                        </Typography>
                        <LinearProgress
                            variant="determinate"
                            value={calculateOverallProgress()}
                            sx={{ mt: 1, mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {calculateOverallProgress()}% abgeschlossen
                        </Typography>
                    </Box>
                </Box>

                <Divider />

                <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                    {lessonGroups.map((group) => (
                    <React.Fragment key={group.key}>
                    {group.title !== null && (
                        <ListSubheader disableSticky sx={{ lineHeight: '32px', fontWeight: 700, color: 'var(--oa-text-2)', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '.05em' }}>
                            {group.title}
                        </ListSubheader>
                    )}
                    {group.entries.map(({ lesson, index }) => {
                        const progress = lessonProgress.get(lesson.id);
                        const isActive = index === currentLessonIndex;
                        const locked = isLessonLocked(index);

                        return (
                            <ListItem key={lesson.id} disablePadding>
                                <ListItemButton
                                    selected={isActive}
                                    disabled={locked}
                                    onClick={() => {
                                        if (locked) {
                                            notify('Bitte schließe zuerst die vorherige Lektion ab.', { type: 'info' });
                                            return;
                                        }
                                        setCurrentLessonIndex(index);
                                    }}
                                >
                                    <ListItemIcon>
                                        {locked ? <LockIcon color="disabled" /> : getStatusIcon(lesson.id)}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                                                    {index + 1}. {lesson.title}
                                                </Typography>
                                                {lesson.isMandatory && (
                                                    <Chip label="Pflicht" size="small" color="error" />
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            progress && progress.status !== 'not_started' ? (
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progress.progressPercentage}
                                                    sx={{ mt: 0.5 }}
                                                />
                                            ) : null
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                    </React.Fragment>
                    ))}
                </List>
            </Drawer>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: drawerOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
                    transition: 'width 0.3s'
                }}
            >
                {/* Top Bar */}
                <AppBar position="static" color="default" elevation={1}>
                    <Toolbar>
                        {!drawerOpen && (
                            <IconButton
                                edge="start"
                                onClick={() => setDrawerOpen(true)}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Typography variant="h6" sx={{ flexGrow: 1 }} noWrap>
                            {currentLesson?.title}
                        </Typography>
                        {ratingSummary && ratingSummary.ratingCount > 0 && (
                            <Box sx={{ mr: 2 }}>
                                <RatingDisplay
                                    averageRating={ratingSummary.averageRating}
                                    ratingCount={ratingSummary.ratingCount}
                                    size="small"
                                />
                            </Box>
                        )}
                        <Tooltip title="Notizen">
                            <IconButton
                                onClick={() => {
                                    if (rightDrawerOpen && rightDrawerTab === 'notes') {
                                        setRightDrawerOpen(false);
                                    } else {
                                        setRightDrawerTab('notes');
                                        setRightDrawerOpen(true);
                                    }
                                }}
                                color={rightDrawerOpen && rightDrawerTab === 'notes' ? 'primary' : 'default'}
                            >
                                <NoteIcon />
                            </IconButton>
                        </Tooltip>
                        {isAiTutorEnabled() && (
                            <Tooltip title="KI-Lernassistent">
                                <IconButton
                                    onClick={() => {
                                        if (rightDrawerOpen && rightDrawerTab === 'tutor') {
                                            setRightDrawerOpen(false);
                                        } else {
                                            setRightDrawerTab('tutor');
                                            setRightDrawerOpen(true);
                                        }
                                    }}
                                    color={rightDrawerOpen && rightDrawerTab === 'tutor' ? 'primary' : 'default'}
                                >
                                    <TutorIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Button
                            startIcon={<BackIcon />}
                            onClick={() => isPreviewMode ? navigate(-1) : navigate('/my-courses')}
                        >
                            {isPreviewMode ? 'Vorschau beenden' : 'Kurs verlassen'}
                        </Button>
                    </Toolbar>
                </AppBar>

                {/* Preview Mode Banner */}
                {isPreviewMode && (
                    <Alert severity="info" sx={{ m: 2 }}>
                        <strong>Vorschau-Modus:</strong> Sie befinden sich im Vorschau-Modus. Ihr Fortschritt wird nicht gespeichert.
                    </Alert>
                )}

                {/* Assignment Info Banner */}
                {assignmentInfo && (assignmentInfo.notes || assignmentInfo.dueDate) && (
                    <Alert severity="info" sx={{ m: 2 }}>
                        {assignmentInfo.dueDate && (
                            <strong>Fällig bis: {new Date(assignmentInfo.dueDate).toLocaleDateString('de-DE')} </strong>
                        )}
                        {assignmentInfo.notes && (
                            <span>— {assignmentInfo.notes}</span>
                        )}
                    </Alert>
                )}

                {/* Lesson Content or Completion Screen */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                    {showCompletion ? (
                        (() => {
                            const completedCount = lessons.filter(l => lessonProgress.get(l.id)?.status === 'completed').length;
                            const totalTimeSpent = Array.from(lessonProgress.values()).reduce((sum, p: any) => sum + (p.timeSpentSeconds || 0), 0);
                            const avgScore = (() => {
                                const scores = Array.from(lessonProgress.values()).map((p: any) => parseFloat(p.score)).filter(s => !isNaN(s) && s > 0);
                                return scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
                            })();
                            const formatTime = (seconds: number) => {
                                const h = Math.floor(seconds / 3600);
                                const m = Math.floor((seconds % 3600) / 60);
                                if (h > 0) return `${h} Std. ${m} Min.`;
                                return `${m} Min.`;
                            };

                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
                                    <TrophyIcon sx={{ fontSize: 80, color: '#FFD700', mb: 2 }} />
                                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                        Kurs abgeschlossen!
                                    </Typography>
                                    <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                                        {course?.title}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 4, mb: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 2, minWidth: 140 }}>
                                            <LessonsIcon sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>{completedCount}/{lessons.length}</Typography>
                                            <Typography variant="body2" color="text.secondary">Lektionen</Typography>
                                        </Box>
                                        {totalTimeSpent > 0 && (
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 2, minWidth: 140 }}>
                                                <TimeIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{formatTime(totalTimeSpent)}</Typography>
                                                <Typography variant="body2" color="text.secondary">Lernzeit</Typography>
                                            </Box>
                                        )}
                                        {avgScore !== null && (
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 2, minWidth: 140 }}>
                                                <ScoreIcon sx={{ fontSize: 36, color: 'warning.main', mb: 1 }} />
                                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{avgScore}%</Typography>
                                                <Typography variant="body2" color="text.secondary">Durchschnitt</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f0f7ff', borderRadius: 2, minWidth: 140 }}>
                                            <CompletedIcon sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
                                            <Typography variant="h5" sx={{ fontWeight: 700 }}>100%</Typography>
                                            <Typography variant="body2" color="text.secondary">Fortschritt</Typography>
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => setShowCompletion(false)}
                                        >
                                            Lektionen nochmal ansehen
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => navigate('/my-courses')}
                                            sx={{ px: 4 }}
                                        >
                                            Kurs beenden
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })()
                    ) : currentLesson && identity ? (
                        <LessonPlayer
                            key={currentLesson.id}
                            lesson={currentLesson}
                            courseId={course?.id}
                            userId={typeof identity.id === "number" ? identity.id : parseInt(identity.id)}
                            userEmail={identity.email || identity.fullName || ''}
                            onComplete={handleLessonComplete}
                            onProgressUpdate={updateLessonProgress}
                        />
                    ) : null}
                </Box>

                {/* Bottom Navigation */}
                {!showCompletion && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        startIcon={<BackIcon />}
                        onClick={goToPreviousLesson}
                        disabled={currentLessonIndex === 0}
                    >
                        Vorherige Lektion
                    </Button>

                    <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                        Lektion {currentLessonIndex + 1} von {lessons.length}
                    </Typography>

                    <Button
                        endIcon={<NextIcon />}
                        onClick={goToNextLesson}
                        disabled={currentLessonIndex === lessons.length - 1 || isLessonLocked(currentLessonIndex + 1)}
                        variant="contained"
                    >
                        Nächste Lektion
                    </Button>
                </Box>
                )}
            </Box>

            {/* Right Sidebar (Notes / Tutor) */}
            <Drawer
                variant="persistent"
                anchor="right"
                open={rightDrawerOpen}
                sx={{
                    width: rightDrawerOpen ? rightDrawerWidth : 0,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: rightDrawerWidth,
                        top: `${TOP_NAVBAR_HEIGHT}px`,
                        height: `calc(100vh - ${TOP_NAVBAR_HEIGHT}px - ${BOTTOM_NAVBAR_HEIGHT}px)`,
                    }
                }}
            >
                {/* Resize handle */}
                <Box
                    onMouseDown={handleResizeMouseDown}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 5,
                        cursor: 'col-resize',
                        zIndex: 10,
                        '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                    }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={rightDrawerTab === 'tutor' && !isAiTutorEnabled() ? 'notes' : rightDrawerTab}
                            onChange={(_, newValue) => setRightDrawerTab(newValue)}
                            sx={{ minHeight: 48 }}
                        >
                            <Tab
                                icon={<NoteIcon fontSize="small" />}
                                iconPosition="start"
                                label="Notizen"
                                value="notes"
                                sx={{ minHeight: 48, textTransform: 'none' }}
                            />
                            {isAiTutorEnabled() && (
                                <Tab
                                    icon={<TutorIcon fontSize="small" />}
                                    iconPosition="start"
                                    label="KI-Tutor"
                                    value="tutor"
                                    sx={{ minHeight: 48, textTransform: 'none' }}
                                />
                            )}
                        </Tabs>
                        <IconButton onClick={() => setRightDrawerOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        {rightDrawerTab === 'notes' && course && (
                            <NoteSidebar
                                courseId={course.id}
                                lessonId={currentLesson?.id}
                                lessonTitle={currentLesson?.title}
                            />
                        )}
                        {rightDrawerTab === 'tutor' && course && isAiTutorEnabled() && (
                            <TutorChat
                                courseId={course.id}
                                lessonId={currentLesson?.id}
                                lessonTitle={currentLesson?.title}
                            />
                        )}
                    </Box>
                </Box>
            </Drawer>

            {course && (
                <RatingDialog
                    open={ratingPromptOpen}
                    courseId={course.id}
                    courseName={course.title}
                    onClose={() => setRatingPromptOpen(false)}
                    onSubmit={() => setRatingPromptOpen(false)}
                />
            )}
        </Box>
    );
};

export default CoursePlayer;
